import React, { useState, useRef, useEffect, useCallback } from 'react';

const LANGUAGES = [
  { id: 'python', label: 'Python', icon: '🐍' },
  { id: 'java',   label: 'Java',   icon: '☕' },
];

const LEVELS = [
  { id: 'sl', label: 'SL', icon: '🟢' },
  { id: 'hl', label: 'HL', icon: '⚡' },
];

/**
 * One component, two chrome styles — the course-track (language + level) picker.
 *
 * variant="chip" (default): a small pill showing the current track that opens a
 * popover to change it. Lives in the dashboard header.
 * variant="onboarding": the same pick-and-save body with no chip/popover shell,
 * for the first-login modal.
 */
export default function TrackPicker({ language, level, onSave, variant = 'chip' }) {
  const [open,         setOpen]         = useState(false);
  const [pendingLang,  setPendingLang]  = useState(language || 'python');
  const [pendingLevel, setPendingLevel] = useState(level    || 'hl');
  const [saving,       setSaving]       = useState(false);
  const popoverRef = useRef(null);

  useEffect(() => {
    setPendingLang(language || 'python');
    setPendingLevel(level || 'hl');
  }, [language, level]);

  useEffect(() => {
    if (variant !== 'chip' || !open) return;
    function handleOutside(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open, variant]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await onSave(pendingLang, pendingLevel);
    } finally {
      setSaving(false);
      if (variant === 'chip') setOpen(false);
    }
  }, [pendingLang, pendingLevel, onSave, variant]);

  const changed = pendingLang !== (language || 'python') || pendingLevel !== (level || 'hl');

  const body = (
    <div className="track-picker-body">
      <div className="track-picker-group">
        <div className="track-picker-group-label">Language</div>
        <div className="track-picker-options">
          {LANGUAGES.map(l => (
            <button
              key={l.id}
              type="button"
              className={`track-picker-opt${pendingLang === l.id ? ' track-picker-opt-active' : ''}`}
              onClick={() => setPendingLang(l.id)}
              aria-pressed={pendingLang === l.id}
            >
              <span aria-hidden="true">{l.icon}</span> {l.label}
            </button>
          ))}
        </div>
      </div>
      <div className="track-picker-group">
        <div className="track-picker-group-label">Level</div>
        <div className="track-picker-options">
          {LEVELS.map(l => (
            <button
              key={l.id}
              type="button"
              className={`track-picker-opt${pendingLevel === l.id ? ' track-picker-opt-active' : ''}`}
              onClick={() => setPendingLevel(l.id)}
              aria-pressed={pendingLevel === l.id}
            >
              <span aria-hidden="true">{l.icon}</span> {l.label}
            </button>
          ))}
        </div>
      </div>
      <button
        type="button"
        className="track-picker-save"
        onClick={handleSave}
        disabled={saving || (variant === 'chip' && !changed)}
      >
        {saving ? 'Saving…' : variant === 'onboarding' ? 'Start learning' : 'Save'}
      </button>
    </div>
  );

  if (variant === 'onboarding') {
    return body;
  }

  const langMeta  = LANGUAGES.find(l => l.id === (language || 'python'));
  const levelMeta = LEVELS.find(l => l.id === (level || 'hl'));

  return (
    <div className="track-picker" ref={popoverRef}>
      <button
        type="button"
        className="track-chip"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-label={`Course track: ${langMeta?.label} · ${levelMeta?.label}. Click to change.`}
      >
        <span aria-hidden="true">{langMeta?.icon}</span>
        {langMeta?.label} · {levelMeta?.label}
        <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor"
          strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="4 6 8 10 12 6" />
        </svg>
      </button>
      {open && (
        <div className="track-picker-popover" role="dialog" aria-label="Change your course track">
          {body}
        </div>
      )}
    </div>
  );
}
